export const HIBP_RANGE_API_URL = 'https://api.pwnedpasswords.com/range/';

async function sha1HexUpper(input: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-1', new TextEncoder().encode(input));
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0'))
    .join('')
    .toUpperCase();
}

/**
 * How many times `password` appears in known breaches, via Have I Been Pwned's
 * k-anonymity range API. Only the first 5 characters of the SHA-1 hash leave the
 * browser — the password and its full hash never do; HIBP returns every breached
 * suffix under that prefix and we match locally.
 *
 * Throws on network / non-2xx response so the caller can decide how to degrade
 * (this feature fails open — see `usePasswordValidation`).
 */
export async function getPwnedCount(password: string): Promise<number> {
  const hash = await sha1HexUpper(password);
  const prefix = hash.slice(0, 5);
  const suffix = hash.slice(5);

  // Add-Padding pads the response to a uniform size with decoy `SUFFIX:0` rows,
  // so a network observer can't infer the match from the response length.
  const response = await fetch(`${HIBP_RANGE_API_URL}${prefix}`, {
    headers: { 'Add-Padding': 'true' },
  });
  if (!response.ok) {
    throw new Error(`HIBP range request failed with status ${response.status}`);
  }

  const body = await response.text();
  for (const line of body.split('\n')) {
    const [lineSuffix, count] = line.trim().split(':');
    if (lineSuffix !== suffix) {
      continue;
    }
    // A count of 0 is a padding decoy, not a real breach hit — treat as not found.
    const parsed = Number(count);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
  }
  return 0;
}
